import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Navigation from '../../components/Navigation/Navigation';
import { $apiV1 } from '../../config/api';
import { getUserProfile } from '../../helpers/userProfile';
import { MainWrapper, RightContent } from '../../style/DasboardMain';

const MAX_SIZE = 6 * 1024 * 1024 * 1024;
const PART_SIZE = 64 * 1024 * 1024;
// The API answers 202 and validates on a queue, so the outcome has to be polled.
// Status polling is rate limited to 60 requests a minute; 5s is well inside it.
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const ExternalVideoUpload = () => {
  const [allowed, setAllowed] = useState(null);
  const [file, setFile] = useState(null);
  const [matchDetails, setMatchDetails] = useState({
    team_a_name: '',
    team_b_name: '',
    competition_name: '',
    match_date: '',
  });
  const [lineupsText, setLineupsText] = useState('');
  const [lineupsPdf, setLineupsPdf] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');

  const selectFile = (selectedFile) => {
    setFile(selectedFile || null);
    setMessage('');
  };

  const updateMatchDetail = (event) => {
    const { name, value } = event.target;
    setMatchDetails((details) => ({ ...details, [name]: value }));
  };

  const selectLineupsPdf = (selectedFile) => {
    if (selectedFile && selectedFile.type !== 'application/pdf') {
      setLineupsPdf(null);
      setMessage('The lineup document must be a PDF.');
      return;
    }
    setLineupsPdf(selectedFile || null);
    setMessage('');
  };

  useEffect(() => {
    getUserProfile().then((response) => setAllowed(Boolean(response.data?.external_video_upload_enabled))).catch(() => setAllowed(false));
  }, []);

  // Returns the settled upload, or null if it is still validating when we stop
  // waiting. Never throws on a rejection: a rejected file is an answer, not an
  // error, and the caller shows the reason the API gave.
  const awaitValidation = async (uploadId, initialStatus) => {
    if (initialStatus && initialStatus !== 'validating') return { status: initialStatus };
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await wait(POLL_INTERVAL_MS);
      const { data } = await $apiV1.get(`/external-video-uploads/${uploadId}`);
      if (data.status !== 'validating') return data;
    }
    return null;
  };

  const upload = async () => {
    if (!file || isUploading) return;
    if (!file.type.startsWith('video/') || file.size > MAX_SIZE) {
      setMessage('Choose a supported video file no larger than 6 GB.');
      return;
    }
    if (Object.values(matchDetails).some((value) => !value.trim())) {
      setMessage('Enter both teams, the competition, and the match date.');
      return;
    }
    setIsUploading(true); setProgress(0); setMessage('');
    try {
      const formData = new FormData();
      formData.append('filename', file.name);
      formData.append('content_type', file.type);
      formData.append('file_size', String(file.size));
      Object.entries(matchDetails).forEach(([name, value]) => formData.append(name, value));
      if (lineupsText.trim()) formData.append('lineups_text', lineupsText.trim());
      if (lineupsPdf) formData.append('lineups_pdf', lineupsPdf);
      const initiated = await $apiV1.post('/external-video-uploads/initiate', formData);
      const uploadId = initiated.data.upload_id;
      const totalParts = Math.ceil(file.size / PART_SIZE);
      const parts = [];
      for (let index = 0; index < totalParts; index += 1) {
        const partNumber = index + 1;
        const signed = await $apiV1.post(`/external-video-uploads/${uploadId}/part-url`, { part_number: partNumber });
        const blob = file.slice(index * PART_SIZE, Math.min(file.size, (index + 1) * PART_SIZE));
        const response = await axios.put(signed.data.url, blob);
        const etag = String(response.headers.etag || '').replaceAll('"', '');
        if (!etag) throw new Error('Upload failed: S3 did not return an ETag.');
        parts.push({ part_number: partNumber, etag });
        setProgress(Math.round((partNumber / totalParts) * 100));
      }
      setMessage('Validating video...');
      const completed = await $apiV1.post(`/external-video-uploads/${uploadId}/complete`, { parts });
      const settled = await awaitValidation(uploadId, completed.data?.status);
      if (!settled) {
        setMessage('Upload received. Validation is taking longer than usual — the SmrtStats team will review it.');
      } else if (settled.status === 'valid') {
        setMessage('Video uploaded successfully.'); setFile(null);
      } else {
        setMessage(settled.error || 'The uploaded file was rejected. Please check the file and try again.');
      }
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'Upload failed. Please try again.');
    } finally { setIsUploading(false); }
  };

  if (allowed === null) return <MainWrapper><Navigation /><RightContent><Page><p>Loading upload access...</p></Page></RightContent></MainWrapper>;
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <MainWrapper><Navigation /><RightContent><Page><Card><h1>External Video Upload</h1><p>Upload a supported video file up to 6 GB.</p><MatchDetails>
    <InputLabel>Team A<input name="team_a_name" type="text" value={matchDetails.team_a_name} disabled={isUploading} onChange={updateMatchDetail} placeholder="e.g. Red Star" required /></InputLabel>
    <InputLabel>Team B<input name="team_b_name" type="text" value={matchDetails.team_b_name} disabled={isUploading} onChange={updateMatchDetail} placeholder="e.g. Partizan" required /></InputLabel>
    <InputLabel>Competition<input name="competition_name" type="text" value={matchDetails.competition_name} disabled={isUploading} onChange={updateMatchDetail} placeholder="e.g. Serbian SuperLiga" required /></InputLabel>
    <InputLabel>Match date<input name="match_date" type="date" value={matchDetails.match_date} disabled={isUploading} onChange={updateMatchDetail} required /></InputLabel>
  </MatchDetails><LineupsDetails>
    <InputLabel>Lineups (optional)<textarea value={lineupsText} disabled={isUploading} onChange={(event) => setLineupsText(event.target.value)} placeholder="Starting XI: ..." /></InputLabel>
    <InputLabel>Lineup PDF (optional)<PdfInput><input type="file" accept="application/pdf,.pdf" disabled={isUploading} onChange={(event) => selectLineupsPdf(event.target.files?.[0])} />{lineupsPdf ? lineupsPdf.name : 'Choose PDF'}</PdfInput></InputLabel>
  </LineupsDetails><DropZone htmlFor="external-video-file" $disabled={isUploading} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); if (!isUploading) selectFile(event.dataTransfer.files?.[0]); }}>
    <input id="external-video-file" type="file" accept="video/mp4,video/quicktime,video/x-matroska,video/webm,video/x-msvideo" disabled={isUploading} onChange={(event) => selectFile(event.target.files?.[0])} />
    <UploadIcon>↑</UploadIcon><strong>Drag or add file</strong><span>MP4, MOV, MKV, WebM or AVI · Maximum 6 GB</span>
    {file && <FileName>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</FileName>}
  </DropZone>
    {isUploading && <ProgressTrack aria-label={`Upload progress: ${progress}%`}><ProgressFill style={{ width: `${progress}%` }} /><ProgressLabel>{progress}% uploaded</ProgressLabel><ProgressLabel $onFill style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}>{progress}% uploaded</ProgressLabel></ProgressTrack>}
    {message && <Message>{message}</Message>}
    <button type="button" disabled={!file || isUploading} onClick={upload}>{isUploading ? 'Uploading...' : 'Upload video'}</button>
  </Card></Page></RightContent></MainWrapper>;
};

const Page = styled.main`min-height:100%; display:flex; align-items:center; justify-content:center; padding:60px 32px 120px;`;
const Card = styled.section`width:min(680px,100%); padding:36px; border-radius:8px; background:${p => p.theme.black}; border:1px solid ${p => p.theme.borderColor}; box-shadow:3px 2px 30px rgba(68,68,68,.12); display:grid; gap:18px; color:${p => p.theme.white}; h1{margin:0;font-family:SpaceGrotesk;font-size:36px;} p{margin:0;color:${p => p.theme.blueish};} button{padding:12px 18px;border:0;border-radius:5px;background:${p => p.theme.green};color:${p => p.theme.black};font-weight:700;cursor:pointer;} button:disabled{opacity:.6;cursor:default;}`;
const MatchDetails = styled.div`display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; @media(max-width:560px){grid-template-columns:1fr;}`;
const LineupsDetails = styled.div`display:grid; gap:14px;`;
const InputLabel = styled.label`display:grid; gap:7px; color:${p => p.theme.white}; font-size:14px; input,textarea{box-sizing:border-box; width:100%; min-height:42px; padding:10px 12px; border:1px solid ${p => p.theme.borderColor}; border-radius:5px; background:${p => p.theme.sidebar}; color:${p => p.theme.white}; font:inherit;} textarea{min-height:84px; resize:vertical;} input:focus,textarea:focus{outline:1px solid ${p => p.theme.green}; border-color:${p => p.theme.green};} input:disabled,textarea:disabled{opacity:.6;}`;
const PdfInput = styled.span`box-sizing:border-box; width:100%; height:42px; padding:10px 12px; border:1px solid ${p => p.theme.borderColor}; border-radius:5px; background:${p => p.theme.sidebar}; color:${p => p.theme.blueish}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:pointer; input{display:none;}`;
const DropZone = styled.label`min-height:180px; border:1px dashed ${p => p.theme.green}; border-radius:6px; background:${p => p.theme.sidebar}; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; cursor:${p => p.$disabled ? 'default' : 'pointer'}; color:${p => p.theme.white}; text-align:center; padding:20px; opacity:${p => p.$disabled ? .6 : 1}; input{display:none;} span{color:${p => p.theme.blueish}; font-size:14px;} &:hover{border-style:solid;}`;
const UploadIcon = styled.div`width:38px;height:38px;border-radius:50%;display:grid;place-items:center;background:${p => p.theme.green};color:${p => p.theme.black};font-size:24px;font-weight:700;`;
const FileName = styled.div`margin-top:6px;color:${p => p.theme.green};font-size:14px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const ProgressTrack = styled.div`height:38px; position:relative; overflow:hidden; border-radius:5px; border:1px solid ${p => p.theme.green}; background:${p => p.theme.sidebar};`;
const liquidFlow = keyframes`from{background-position:0 0;}to{background-position:160px 0;}`;
const ProgressFill = styled.div`height:100%; position:relative; overflow:hidden; background:linear-gradient(105deg,${p => p.theme.green} 0%,${p => p.theme.green} 34%,#d8ff54 50%,${p => p.theme.green} 66%,${p => p.theme.green} 100%); background-size:160px 100%; animation:${liquidFlow} 2.8s linear infinite; transition:width .7s cubic-bezier(.16, 1, .3, 1); will-change:width;`;
const ProgressLabel = styled.span`position:absolute; inset:0; display:grid; place-items:center; color:${p => p.$onFill ? p.theme.black : p.theme.white}; font-family:SatoshiBold; font-size:14px; pointer-events:none;`;
const Message = styled.div`color:${p => p.theme.blueish};`;

export default ExternalVideoUpload;
